import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setTitle } from '../../redux/title/action';
import { Space, Spin, Row, Col, Button, Divider } from 'antd';
import { ImageTheme } from '../../components/images';
import barcode from '../../assets/images/exBarcode.png';
import { RenderForm } from '../../components/forms';
import { useGetRegisterList } from './hooks/useGetRegisterList';
import { useVerifyQRCode } from './hooks/useGetBarcodeQuery';
import { FORM_BARCODE, FORM_QR_CODE } from './form';
import { TextLarge, TextSmall, TextCustom } from '../../components/text';
import {
  InfoHeaderStyledContainer,
  RegisterStyledContainer,
} from '../../components/container/styles';
import { barcodeCard, qrScanButtonStyle } from './styles/styles';
import liff from '@line/liff';
import { FaQrcode } from 'react-icons/fa';
import { openAlert, openLoading, closeLoading } from '../../components/alert/hooks/useAlert';
import { QRScannerModal } from './modal/QRScannerModal';

export const RegisterQR = () => {
  // get warrantyNumberCode from query params
  const queryParams = new URLSearchParams(window.location.search);
  const initialWarrantyNumberCode = queryParams.get('fw');
  const [warrantyNumberCode, setWarrantyNumberCode] = useState(initialWarrantyNumberCode);
  const { control, getValues, setValue, handleOnEnterBarcode, isLoadingBarcode } =
    useGetRegisterList(warrantyNumberCode);
  const { mutate: verifyQRCode, loading: isVerifyingQR } = useVerifyQRCode();
  const dispatch = useDispatch();
  const [isScanning, setIsScanning] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const hasVerifiedInitialCode = useRef(false);

  useEffect(() => {
    dispatch(setTitle('ลงทะเบียนรับประกันสินค้า'));
  }, [dispatch]);

  // Verify warrantyNumberCode when page first loads if it exists in query params
  useEffect(() => {
    if (initialWarrantyNumberCode && !hasVerifiedInitialCode.current) {
      hasVerifiedInitialCode.current = true;
      // Validate and verify the initial warrantyNumberCode
      let codeToVerify = initialWarrantyNumberCode;

      // Check if it's a URL and extract fw parameter
      if (initialWarrantyNumberCode.includes('http')) {
        try {
          const url = new URL(initialWarrantyNumberCode);
          const params = url.searchParams;
          codeToVerify = params.get('fw') || initialWarrantyNumberCode;
        } catch (error) {
          console.error('Error parsing URL:', error);
        }
      }

      // Verify the code
      verifyQRCode(codeToVerify, {
        onSuccess: () => {
          // Set warrantyNumberCode when verification is successful
          setWarrantyNumberCode(codeToVerify);
        },
        onError: () => {
          // If verification fails, clear the code
          setWarrantyNumberCode(null);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialWarrantyNumberCode]);

  const handleScanQRCode = async () => {
    // Check if in LINE client - use LINE LIFF scanner
    try {
      if (liff.isInClient() && liff.scanCode) {
        // Use LINE LIFF scanner (mobile)
        setIsScanning(true);
        openLoading();

        try {
          const result = await liff.scanCode();
          if (result && result.value) {
            let scannedCode = result.value;
            // validate scannedCode is url or not
            if (scannedCode.includes('http')) {
              // get query params from url
              const url = new URL(scannedCode);
              const params = url.searchParams;
              scannedCode = params.get('fw') || scannedCode;
            }
            handleLineQRScanned(scannedCode);
          }
        } catch (error) {
          console.error('QR Scan Error:', error);
          // Handle user cancellation
          if (error.message && !error.message.includes('cancel')) {
            openAlert({
              type: 'error',
              message: error?.message || 'เกิดข้อผิดพลาดในการสแกน QR code',
            });
          }
        } finally {
          setIsScanning(false);
          closeLoading();
        }
      } else {
        // Use webcam scanner (desktop/computer)
        setShowQRModal(true);
      }
    } catch (error) {
      console.error('LIFF check error:', error);
      // Fallback to webcam scanner if LIFF check fails
      setShowQRModal(true);
    }
  };

  const handleQRDetected = (scannedCode) => {
    verifyQRCode(scannedCode, {
      onSuccess: () => {
        // Set warrantyNumberCode when verification is successful
        setWarrantyNumberCode(scannedCode);
      },
    });
    setShowQRModal(false);
  };

  const handleLineQRScanned = (scannedCode) => {
    verifyQRCode(scannedCode, {
      onSuccess: () => {
        // Set warrantyNumberCode when verification is successful
        setWarrantyNumberCode(scannedCode);
        setShowQRModal(false);
      },
    });
  };

  const handleOnEnterQRCode = (qrCode) => {
    verifyQRCode(qrCode, {
      onSuccess: () => {
        // Set warrantyNumberCode when verification is successful
        setWarrantyNumberCode(qrCode);
      },
    });
  };

  return (
    <RegisterStyledContainer>
      <Spin spinning={isLoadingBarcode || isVerifyingQR}>
        <div>
          {!warrantyNumberCode && (
            <InfoHeaderStyledContainer>
              <TextSmall
                text={
                  <span>
                    กรุณาสแกน QR Code รับประกันสินค้า
                  </span>
                }
                style={{
                  color: '#8c6e19',
                }}
                bold
              />
            </InfoHeaderStyledContainer>
          )}
          {!warrantyNumberCode && <div>
            <Row style={{ margin: '40px' }}>
              <Col span={24}>
                <RenderForm
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                  forms={FORM_QR_CODE({ onEnter: handleOnEnterQRCode })}
                />
              </Col>
            </Row>
            <Row style={{ margin: '0 40px' }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <Divider style={{ flex: 1, margin: 0, minWidth: 0 }} />
                  <TextCustom text="หรือ" bold size="14px" color="grey" style={{ whiteSpace: 'nowrap' }} />
                  <Divider style={{ flex: 1, margin: 0, minWidth: 0 }} />
                </div>
              </Col>
            </Row>
            <Row style={{ margin: 40 }}>
              <Col span={24}>
                <Button
                  icon={<FaQrcode size={36} />}
                  onClick={handleScanQRCode}
                  loading={isScanning}
                  size="large"
                  style={qrScanButtonStyle}
                >
                  สแกน QR Code
                </Button>
              </Col>
            </Row>
          </div>}

        </div>

        {warrantyNumberCode && (
          <div>
            <InfoHeaderStyledContainer>
              <TextSmall
                text={
                  <span>
                    กรอกรหัส <span style={{ color: '#000' }}>Barcode 9 หลักสุดท้าย</span> ของสินค้า
                  </span>
                }
                style={{
                  color: 'red',
                }}
                bold
              />
            </InfoHeaderStyledContainer>
            <Row style={{ marginTop: 20, marginBottom: 20 }}>
              <Col span={24} style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <TextCustom text="รหัสประกันสินค้า: " bold size="16px" />
                <TextLarge text={warrantyNumberCode} style={{ color: '#000' }} />
              </Col>
            </Row>
            <Divider />
            <Space direction="vertical" align="center" style={{ width: '100%', marginTop: 20 }}>
              <div style={{ textAlign: 'center' }}>
                <TextSmall text="ตัวอย่าง Barcode" bold />
              </div>
              <div style={barcodeCard}>
                <ImageTheme src={barcode} style={{ width: 200 }} />
              </div>
            </Space>
            <div style={{ padding: '10px 40px 40px 40px' }}>
              <RenderForm
                control={control}
                getValues={getValues}
                setValue={setValue}
                forms={FORM_BARCODE({ onEnter: handleOnEnterBarcode })}
              />
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <TextCustom text="กรุณาสแกน barcode ของสินค้า" bold size="24px" color="red" />
              </div>
            </div>
          </div>
        )}
      </Spin>
      <QRScannerModal
        open={showQRModal}
        onClose={() => setShowQRModal(false)}
        onDetected={handleQRDetected}
      />
    </RegisterStyledContainer>
  );
};
